/**
 * Composable Report Widget — type contracts.
 * Co-located with the widget that implements them.
 * See scratch/COMPOSABLE_REPORT_WIDGET_PLAN.md §4.
 */
import { CurveType } from 'recharts/types/shape/Curve';

import { ProductMetricsDto } from 'entities/ReportSavingsSummary';

import type { ReactNode } from 'react';
import type { TooltipProps } from 'recharts';

/** Chart type for the widget visualization. */
export type ReportWidgetChartType =
    | 'bar'
    | 'pie'
    | 'donut'
    | 'area'
    | 'radar'
    | 'line';

/** Known optional fields on item.meta used by the composable widget (chart hover, non-interactive "Other"). */
export interface ReportWidgetItemMeta extends Record<string, unknown> {
    /** When set, row hover highlights this chart slice (e.g. "Other" for rows beyond top N). */
    chartItemId?: string;
    /** When true, pie/donut slice is not clickable (e.g. aggregated "Other" wedge). */
    isAggregatedOther?: boolean;
    /** Number of rows aggregated into the synthetic "Other" pie/donut slice. */
    aggregatedCount?: number;
    /** When set, tooltip displays product metrics for savings summary report. */
    productMetrics?: ProductMetricsDto;
}

/** Single item shape consumed by both chart and table. */
export interface ReportWidgetItem {
    id: string;
    label: string;
    primaryValue: number | string;
    secondaryValues?: Record<string, number | string>;
    color?: string;
    /** Raw/report-specific data for adapters (e.g. drill-in, details panel). */
    meta?: ReportWidgetItemMeta;
}

/** Id used for chart highlight (row hover). Uses meta.chartItemId when present so "Other" rows map to the Other slice. */
export function getChartItemId(item: ReportWidgetItem): string {
    return item.meta?.chartItemId ?? item.id;
}

export interface ReportWidgetTooltipRowConfig {
    id: string;
    variant: 'header' | 'labelValue' | 'divider';
    label?: string;
    value?: string;
    boldLabel?: boolean;
    boldValue?: boolean;
    mutedLabel?: boolean;
    positiveValue?: boolean;
    labelColor?: string;
    valueColor?: string;
    rawAmount?: number;
    showSwatch?: boolean;
    swatchColor?: string;
    marginTop?: number;
    marginBottom?: number;
}

export interface ReportWidgetTooltipArgs {
    payload?: TooltipProps<number, string>['payload'];
    label?: string;
    valueFormatter: (value: number) => string;
    /** Chart items used to resolve tooltip content. */
    items: ReportWidgetItem[];
    /** Optional total for computing percentages. */
    total?: number;
    /**
     * Optional aggregation context for special slices (e.g. pie "Other").
     * Allows report-specific tooltip builders to control label/value rows.
     */
    aggregation?: {
        kind: 'other';
        label: string;
        item: ReportWidgetItem;
        headerRows: ReportWidgetTooltipRowConfig[];
    };
}

export type ReportWidgetTooltipRowsBuilder = (
    args: ReportWidgetTooltipArgs,
) => ReportWidgetTooltipRowConfig[] | null | undefined;

/** Column definition for the widget table. */
export interface ReportWidgetColumnConfig {
    id: string;
    label: string;
    /** Field key on ReportWidgetItem (e.g. primaryValue or key in secondaryValues). */
    valueKey: string;
    format?: 'currency' | 'number' | 'percent' | 'text';
    /** Column alignment. Default: right for number/currency, left otherwise. */
    align?: 'left' | 'right';
    /** Tooltip text for header (e.g. info icon). */
    headerTooltip?: string;
    /** Optional custom text color for highlighted cells (e.g. positive or warning). */
    highlightColor?: string;
    /** When true, cell text in this column is bold (e.g. first/label column). */
    bold?: boolean;
    /** Optional per-cell tooltip computed from the row item (e.g. list of properties). */
    getCellTooltip?: (item: ReportWidgetItem) => string | ReactNode | null;
    /** When true, clicking this column's cell will trigger a details_click interaction (if onDetailsClick is provided). */
    isDetailsTrigger?: boolean;
    /** Optional minimum rendered width for the column, in pixels. */
    minWidth?: number;
    /** Optional maximum rendered width for the column, in pixels. */
    maxWidth?: number;
    /** Allow text wrapping inside this column's cells. */
    allowWrap?: boolean;
    /** Optional line clamp when wrapping is enabled. */
    wrapLines?: number;
    /** When true and table options include onSort, the header is clickable. */
    sortable?: boolean;
}

/** Normalized chart point used by widget visualizations. */
export interface ReportWidgetChartPoint {
    name: string;
    value: number;
    id: string;
    color?: string;
}

/** Interaction types for callbacks. */
export type ReportWidgetInteractionType =
    | 'row_click'
    | 'chart_item_click'
    | 'details_click';

/** Payload passed to report when user interacts (row, chart slice, details icon). */
export interface ReportWidgetInteractionPayload {
    widgetId: string;
    interactionType: ReportWidgetInteractionType;
    itemId: string;
    itemLabel: string;
    item: ReportWidgetItem;
    meta?: Record<string, unknown>;
}

/** Color palette for charts. */
export type ReportWidgetColorPalette = 'neutral' | 'positive' | 'negative';

/** Visualization config. */
export interface ReportWidgetVisualizationOptions {
    showLegend?: boolean;
    valueFormatter?: (value: number | string) => string;
    colors?: string[];
    palette?: ReportWidgetColorPalette;
    /** Minimum pie/donut slice angle in degrees; small slices render at this size for hover. Default 4. */
    pieMinAngle?: number;
    lineNames?: [string, string];
    showSecondSeries?: boolean;
    lineType?: CurveType;
    secondSeriesKey?: string;
    /** Optional custom formatter for the x-axis tick labels. Signature matches Recharts tickFormatter. */
    xAxisTickFormatter?: (
        label: string | number,
        index: number,
        data: unknown[],
    ) => string;
    /** Degrees to rotate X-axis tick labels (Recharts XAxis angle). Bar and line charts. */
    xAxisTickAngle?: number;
    /** Optional X-axis tick wrapping configuration (for long labels). */
    xAxisTickWrap?: {
        maxLines?: number;
        maxCharsPerLine?: number;
    };
    /** Optional custom formatter for the y-axis tick labels. */
    yAxisTickFormatter?: (value: number) => string;
    /** Optional builder for tooltip rows. When omitted, default tooltip layout is used. */
    tooltipRowsBuilder?: ReportWidgetTooltipRowsBuilder;
}

export interface ReportWidgetVisualizationConfig {
    enabled: boolean;
    type: ReportWidgetChartType;
    options?: ReportWidgetVisualizationOptions;
}

/** Table config. */
export interface ReportWidgetTableConfigOptions {
    /** When set, table shows "Show N more" link instead of button; N = loadMoreCount. */
    loadMoreVariant?: 'button' | 'link';
    /** Number to show in "Show N more" link; passed by widget when hasMore. */
    loadMoreCount?: number;
    /** valueKey of the column that shows a color swatch (e.g. 'label'). */
    showColorSwatchInLabelColumn?: string;
    /** When set and items are empty, show this instead of default "No data". */
    emptyMessage?: string;
    /** Column id currently driving table sort. Used with sortable columns. */
    sortColumnId?: string;
    sortDirection?: 'asc' | 'desc';
    onSort?: (columnId: string) => void;
}

export interface ReportWidgetTableConfig {
    enabled: boolean;
    columns: ReportWidgetColumnConfig[];
    options?: ReportWidgetTableConfigOptions;
}

/** Table presentation options passed to ReportWidgetTable (config.options + derived showDetailsColumn). */
export interface ReportWidgetTableOptions
    extends ReportWidgetTableConfigOptions {}

/** Pagination strategy for the widget. */
export type ReportWidgetPagination =
    | {
          mode: 'none';
      }
    | {
          mode: 'client';
          pageSize: number;
          revealAllOnLoadMore?: boolean;
      }
    | {
          mode: 'server';
          pageSize: number;
          canLoadMore: boolean;
          onLoadMore: () => void;
      };

/** System-defined widget config (not user-editable in v0). */
export interface ReportWidgetConfig {
    widgetId: string;
    title: string;
    description?: string;
    /** Optional model label used by chart/table UI copy (e.g. "products"). */
    modelLabel?: string;
    preferredColumnSpan?: number;
    minWidth?: number;
    layout?: 'stacked' | 'sideBySide';
    hasTopLegend?: boolean;
    /** `section` uses theme h2. Default `report` is the 20px / 500 report title. */
    titleVariant?: 'report' | 'section';
    visualization: ReportWidgetVisualizationConfig;
    table?: ReportWidgetTableConfig;
    pagination?: ReportWidgetPagination;
    sortKey?: string;
    sortDirection?: 'asc' | 'desc';
    onRowClick?: (payload: ReportWidgetInteractionPayload) => void;
    onChartItemClick?: (payload: ReportWidgetInteractionPayload) => void;
    onDetailsClick?: (payload: ReportWidgetInteractionPayload) => void;
}

/** Shared presentational props for widget layout variants (stacked/side-by-side). */
export interface ComposableWidgetLayoutProps {
    chartItems: ReportWidgetItem[];
    visibleItems: ReportWidgetItem[];
    visualization: ReportWidgetVisualizationConfig;
    table: ReportWidgetTableConfig;
    modelLabel?: string;
    showChart?: boolean;
    loading: boolean;
    isPrinting: boolean;
    disableChartAnimation: boolean;
    isInitialLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    handleLoadMore: () => void;
    loadMoreCount: number;
    canCollapse: boolean;
    handleCollapse: () => void;
    itemColors: string[];
    rowClickHandler?: (item: ReportWidgetItem) => void;
    detailsClickHandler?: (item: ReportWidgetItem) => void;
    chartItemClickHandler?: (item: ReportWidgetItem) => void;
    hoveredItemId: string | null;
    tableActiveItemId: string | undefined;
    chartHoverHandler: (item: ReportWidgetItem | null) => void;
    tableRowHoverHandler?: (item: ReportWidgetItem | null) => void;
    loadMoreHoverHandler?: (hovering: boolean) => void;
}
